import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ExternalLink, AlertTriangle, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { api } from '../../api/client';

/**
 * E-fakture NAŠE firme na dashboardu (samo uloga admin).
 *
 * Naša firma koristi Efikasu kao klijent, pa server traži nalog sa istim PIB-om
 * kao u „Podacima firme" (`GET /admin/system/own-company-efaktura`).
 */

type Faktura = {
  id: string;
  status: string;
  invoiceNumber: string;
  issueDate: string;
  buyerName: string;
  totalWithVat: string | number;
  isOffline: boolean;
};

type Zbir = { brojMesec: number; iznosMesec: string; naCekanju: number; problem: number; poslednje: Faktura[] };

type Odgovor =
  | { state: 'NO_COMPANY_TAX_ID' }
  | { state: 'NO_TENANT'; taxId: string; companyName: string }
  | {
      state: 'OK';
      tenant: { id: string; name: string };
      taxId: string;
      sefConnected: boolean;
      environment: 'demo' | 'production' | null;
      sameTaxIdTenants: number;
      outbound: Zbir;
      inbound: Zbir;
    };

const STATUS: Record<string, { tekst: string; boja: string }> = {
  draft: { tekst: 'Nacrt', boja: '#94a3b8' },
  validating: { tekst: 'Provera', boja: '#fcd34d' },
  queued: { tekst: 'U redu', boja: '#fcd34d' },
  submitting: { tekst: 'Šalje se', boja: '#fcd34d' },
  submitted: { tekst: 'Primljeno', boja: '#93c5fd' },
  sent: { tekst: 'Poslato', boja: '#93c5fd' },
  approved: { tekst: 'Prihvaćeno', boja: '#6ee7b7' },
  rejected: { tekst: 'Odbijeno', boja: '#fca5a5' },
  submit_failed: { tekst: 'Slanje palo', boja: '#fca5a5' },
  cancelled: { tekst: 'Otkazano', boja: '#94a3b8' },
  storno: { tekst: 'Stornirano', boja: '#94a3b8' },
  issued: { tekst: 'Izdato', boja: '#93c5fd' },
  paid: { tekst: 'Plaćeno', boja: '#6ee7b7' },
};

const dinari = (v: string | number) =>
  Number(v || 0).toLocaleString('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const datum = (d: string) => {
  const [g, m, dan] = String(d).slice(0, 10).split('-');
  return dan ? `${dan}.${m}.${g}.` : d;
};

export function OwnCompanyEfakturaPanel() {
  const [data, setData] = useState<Odgovor | null>(null);
  const [greska, setGreska] = useState<string | null>(null);

  useEffect(() => {
    api.get<Odgovor>('/admin/system/own-company-efaktura')
      .then(setData)
      .catch((e: any) => setGreska(e?.data?.message || e?.message || 'Učitavanje nije uspelo'));
  }, []);

  return (
    <div style={panel} data-testid="own-efaktura-panel">
      <div style={panelHeader}>
        <FileText size={16} style={{ color: '#60a5fa' }} />
        <span>E-fakture naše firme</span>
        {data?.state === 'OK' && (
          <Link to={`/efaktura?tenantId=${data.tenant.id}`} style={panelLink}>Sve fakture <ExternalLink size={11} /></Link>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {greska && <Poruka>{greska}</Poruka>}
        {!greska && !data && <div style={muted}>Učitavanje...</div>}

        {data?.state === 'NO_COMPANY_TAX_ID' && (
          <Poruka>
            PIB naše firme nije upisan. Unesite ga u <Link to="/company">Podaci firme</Link>, pa će se ovde videti fakture.
          </Poruka>
        )}

        {data?.state === 'NO_TENANT' && (
          <Poruka>
            Nijedan klijent nema PIB {data.taxId}{data.companyName ? ` (${data.companyName})` : ''}. E-fakture naše firme
            se vode kroz Efikasu kao kod svakog klijenta. Napravite nalog sa tim PIB-om u <Link to="/tenants">Klijenti</Link> i
            povežite ga sa SEF-om.
          </Poruka>
        )}

        {data?.state === 'OK' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span><strong style={{ color: 'var(--color-text)' }}>{data.tenant.name}</strong> · PIB {data.taxId}</span>
              {data.sefConnected
                ? <Znacka boja="#6ee7b7">SEF povezan{data.environment === 'demo' ? ' (demo)' : ''}</Znacka>
                : <Znacka boja="#fcd34d">SEF nije povezan</Znacka>}
              {data.sameTaxIdTenants > 1 && (
                <span>· {data.sameTaxIdTenants} naloga sa istim PIB-om, prikazan je {data.sefConnected ? 'onaj povezan sa SEF-om' : 'najstariji'}</span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 14 }}>
              <Strana naslov="Izlazne" ikona={<ArrowUpRight size={14} />} zbir={data.outbound} partner="Kupac"
                naCekanju="Nije stiglo na SEF" problem="Odbijeno / palo" />
              <Strana naslov="Ulazne" ikona={<ArrowDownLeft size={14} />} zbir={data.inbound} partner="Dobavljač"
                naCekanju="Čeka odluku" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Strana({ naslov, ikona, zbir, partner, naCekanju, problem }: {
  naslov: string; ikona: React.ReactNode; zbir: Zbir; partner: string; naCekanju: string; problem?: string;
}) {
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px', fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)', background: 'var(--color-bg)' }}>
        {ikona} {naslov}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: problem ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', gap: 8, padding: 12 }}>
        <Broj oznaka="Ovaj mesec" vrednost={`${dinari(zbir.iznosMesec)} RSD`} pod={`${zbir.brojMesec} faktura`} />
        <Broj oznaka={naCekanju} vrednost={String(zbir.naCekanju)} boja={zbir.naCekanju > 0 ? '#fcd34d' : undefined} />
        {problem && <Broj oznaka={problem} vrednost={String(zbir.problem)} boja={zbir.problem > 0 ? '#fca5a5' : undefined} />}
      </div>
      {zbir.poslednje.length === 0 ? (
        <div style={{ ...muted, padding: '0 12px 12px' }}>Nema faktura.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={th}>Broj</th>
                <th style={th}>Datum</th>
                <th style={th}>{partner}</th>
                <th style={{ ...th, textAlign: 'right' }}>Iznos</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {zbir.poslednje.map((f) => {
                const s = STATUS[f.status] ?? { tekst: f.status, boja: '#94a3b8' };
                return (
                  <tr key={f.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={td}><Link to={`/efaktura/invoices/${f.id}`}>{f.invoiceNumber}</Link></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{datum(f.issueDate)}</td>
                    <td style={{ ...td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.buyerName}>{f.buyerName}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{dinari(f.totalWithVat)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: s.boja }}>{s.tekst}{f.isOffline ? ' · van SEF-a' : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Broj({ oznaka, vrednost, pod, boja }: { oznaka: string; vrednost: string; pod?: string; boja?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{oznaka}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: boja ?? 'var(--color-text-strong)', overflowWrap: 'anywhere' }}>{vrednost}</div>
      {pod && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{pod}</div>}
    </div>
  );
}

function Znacka({ boja, children }: { boja: string; children: React.ReactNode }) {
  return <span style={{ padding: '1px 8px', borderRadius: 9, border: `1px solid ${boja}`, color: boja, fontSize: 11 }}>{children}</span>;
}

function Poruka({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
      <AlertTriangle size={14} style={{ color: '#fcd34d', flexShrink: 0, marginTop: 3 }} />
      <div>{children}</div>
    </div>
  );
}

const panel: React.CSSProperties = {
  background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)',
  overflow: 'hidden', marginBottom: 24,
};
const panelHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
  borderBottom: '1px solid var(--color-border)', fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)',
};
const panelLink: React.CSSProperties = {
  marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4,
  fontSize: 11, fontWeight: 500, color: 'var(--color-text-muted)', textDecoration: 'none',
};
const muted: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-muted)' };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '6px 12px', fontSize: 11, fontWeight: 600,
  color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em',
};
const td: React.CSSProperties = { padding: '7px 12px', color: 'var(--color-text)' };
